import { Card, Group, Progress, Stack, Text } from '@mantine/core';
import { BarDistribution } from '@/components/bar-distribution';
import { PageHeader } from '@/components/page-header';
import { SampleDataBanner } from '@/components/sample-data-banner';
import { SectionHeading } from '@/components/section-heading';
import { StatTile } from '@/components/stat-tile';
import { dropOff } from '@/lib/fixtures/analytics';
import classes from './analytics-page.module.css';

const format = new Intl.NumberFormat('en-GB');

function share(part: number, whole: number): string {
  if (whole === 0) return '—';
  return `${((part / whole) * 100).toFixed(0)}%`;
}

interface SplitRowProps {
  label: string;
  value: number;
  total: number;
  color: string;
}

function SplitRow({ label, value, total, color }: SplitRowProps) {
  return (
    <Stack gap={4}>
      <Group justify="space-between" wrap="nowrap" gap="sm">
        <Text size="sm">{label}</Text>
        <Text size="sm" fw={650} className={classes.numeric}>
          {format.format(value)}
          <Text span size="xs" c="dimmed">
            {' '}
            {share(value, total)}
          </Text>
        </Text>
      </Group>
      <Progress value={(value / total) * 100} color={color} size="md" />
    </Stack>
  );
}

export function DropOffPage() {
  const { assessment, registration, trial } = dropOff;

  const earlyBounce = assessment.buckets
    .filter((bucket) => bucket.turn <= 3)
    .reduce((total, bucket) => total + bucket.users, 0);

  const registered = registration.chose + registration.requiredToSubscribe;
  const lostInTrial = trial.cancelledVoluntary + trial.failedInvoluntary;

  return (
    <Stack gap="xl" className={classes.page}>
      <SampleDataBanner />

      <PageHeader
        title="Drop-off"
        description="Conversion is the number; this is the deliverable. Each step broken out by how it failed, not by how much."
        actions={
          <Text size="sm" c="dimmed">
            {dropOff.window}
          </Text>
        }
      />

      <Stack gap="sm">
        <SectionHeading
          eyebrow="Step 2"
          title="Assessment"
          subtitle="Where Act II was abandoned, by the turn the user stopped at."
          note="Bouncing at turn 2 and bouncing at turn 10 are opposite problems: one is the opening, the other is difficulty. Non-completion is recorded as an outcome with a turn index rather than as an absence, because the journey is designed for bouncing."
        />

        <div className={classes.split}>
          <Card>
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                The dashed line is the running total. It clears two thirds
                before turn four, which makes this an opening problem rather
                than a difficulty one.
              </Text>
              <BarDistribution
                cumulative
                caption="Users who abandoned the assessment, by the turn they stopped at, with a running total across the turns."
                axisLabel="Turn the user stopped at"
                bars={assessment.buckets.map((bucket) => ({
                  label: String(bucket.turn),
                  value: bucket.users,
                  emphasis: bucket.turn <= 3,
                }))}
              />
            </Stack>
          </Card>

          <Card>
            <div className={classes.tiles}>
              <StatTile
                variant="plain"
                label="Reached the café"
                value={format.format(assessment.reached)}
              />
              <StatTile
                variant="plain"
                label="Completed"
                value={format.format(assessment.completed)}
                tone="teal"
                secondary={`${share(assessment.completed, assessment.reached)} of those who got there`}
              />
              <StatTile
                variant="plain"
                label="Bounced"
                value={format.format(assessment.bounced)}
                secondary={`${share(earlyBounce, assessment.bounced)} inside the first three turns`}
              />
            </div>
          </Card>
        </div>
      </Stack>

      <Stack gap="sm">
        <SectionHeading
          eyebrow="Step 3"
          title="Registration"
          subtitle="Two splits on the same rung — when it happened, and why."
          note="Somebody who registered in order to subscribe never made an independent decision to register. Counting them in the registration-to-subscription rate inflates it with exactly the people who bypassed the step."
        />

        <div className={classes.split}>
          <Card>
            <Stack gap="md">
              <Text size="xs" fw={600} c="dimmed">
                WHEN
              </Text>
              <SplitRow
                label="In the first session"
                value={registration.inFirstSession}
                total={registered}
                color="brand"
              />
              <SplitRow
                label="Later"
                value={registration.later}
                total={registered}
                color="teal"
              />
              <Text size="xs" c="dimmed">
                Weighted this heavily toward the first session, registration is
                carried by the Act III moment rather than by accumulated value.
              </Text>
            </Stack>
          </Card>

          <Card>
            <Stack gap="md">
              <Text size="xs" fw={600} c="dimmed">
                WHY
              </Text>
              <SplitRow
                label="Chose to"
                value={registration.chose}
                total={registered}
                color="brand"
              />
              <SplitRow
                label="Required in order to subscribe"
                value={registration.requiredToSubscribe}
                total={registered}
                color="magenta"
              />
              <Text size="xs" c="dimmed">
                These accounts and their subscriptions are seconds apart. They
                belong in the rung, but not in its conversion rate.
              </Text>
            </Stack>
          </Card>
        </div>
      </Stack>

      <Stack gap="sm">
        <SectionHeading
          eyebrow="Step 5"
          title="Trial"
          subtitle="The only passive rung — payment happens by default unless somebody acts."
          note="The event carrying information is the cancellation, which lands days before the charge. A failed card is not a decision and does not belong in the same number as one: it is a dunning problem, not a product one."
        />
        <Card>
          <div className={classes.tiles}>
            <StatTile
              variant="plain"
              label="Trials started"
              value={format.format(trial.started)}
            />
            <StatTile
              variant="plain"
              label="Converted"
              value={format.format(trial.converted)}
              tone="teal"
              secondary={`${share(trial.converted, trial.started)} of trials`}
            />
            <StatTile
              variant="plain"
              label="Cancelled"
              value={format.format(trial.cancelledVoluntary)}
              secondary={`${share(trial.cancelledVoluntary, lostInTrial)} of the loss`}
              note="A decision, usually made in the first day or two — long before the charge that would have followed."
            />
            <StatTile
              variant="plain"
              label="Payment failed"
              value={format.format(trial.failedInvoluntary)}
              tone="red"
              secondary={`${share(trial.failedInvoluntary, lostInTrial)} of the loss`}
              note="Not a decision. An expired or declined card. This is a dunning problem and it should never be read as churn."
            />
          </div>
        </Card>
      </Stack>
    </Stack>
  );
}
